import { CATEGORY_OPTIONS } from './data.js';

/**
 * Definitions PARTAGEES entre l'ecran Parc et son ecran de reglages
 * (settings.tsx) : catalogue des filtres des portes, catalogue des stats
 * d'en-tete, cles de prefs et valeurs par defaut. Un seul endroit, zero
 * duplication.
 *
 * Les prefs sont locales au poste (localStorage via `usePref` de @taki/ui) et
 * suivent les cles standard du socle :
 *  - "app.inventaire.filters" : Record<filterId, boolean> (visibilite des filtres) ;
 *  - "app.inventaire.stats"   : string[] (ids ordonnes des stats affichees, max 3).
 */

/**
 * LE CATALOGUE DE FILTRES des portes (gabarit v2) : les cinq CATEGORIES du
 * parc, descendues de l'ancien menu de la zone 1 (la zone 1 dit desormais QUEL
 * JEU, la zone 4 dit QUELLE TRANCHE). Multi-choix ADDITIF : poser "Camera"
 * puis "Optique" AGRANDIT la selection (le vrai besoin d'un loueur :
 * "montre-moi le materiel image"). Il n'y a PAS d'entree "Tout le parc" :
 * l'etat de repos (aucun filtre) montre tout, et l'entree de sortie du popover
 * ("Tout afficher") appartient au chassis. Derive de CATEGORY_OPTIONS
 * (data.ts) : une categorie = un filtre, jamais deux listes a synchroniser.
 * La VISIBILITE de chaque entree est editable dans settings.tsx.
 */
export const FILTER_DEFS: ReadonlyArray<{ id: string; label: string }> = CATEGORY_OPTIONS.map(
  (option) => ({ id: String(option.value), label: option.label }),
);

/**
 * Le libelle du REPLI du catalogue, tel que le popover le rend (entree de
 * sortie du chassis). Les reglages l'affichent en premiere ligne, verrouillee
 * "toujours visible" : ce repli ne se masque pas, c'est lui qui garantit
 * qu'on peut toujours revenir a tout le parc.
 */
export const FILTER_FALLBACK_LABEL = 'Tout afficher';

/** Cle de pref : visibilite des filtres du catalogue. Cle absente = visible. */
export const FILTERS_PREF_KEY = 'app.inventaire.filters';

/** Defaut filtres : tout le catalogue visible. */
export const DEFAULT_FILTERS: Record<string, boolean> = Object.fromEntries(
  FILTER_DEFS.map((def) => [def.id, true]),
);

/** Ids des statistiques disponibles pour l'en-tete (zone 2c). */
export type InventaireStatId =
  | 'total'
  | 'disponible'
  | 'loue'
  | 'maintenance'
  | 'valeur'
  | 'tarif-jour';

/**
 * Catalogue des stats affichables en haut a droite, pour la porte MATERIEL
 * (la porte Maintenance porte sa propre stat fixe, "en maintenance"). La
 * SELECTION (max 3) est editable dans settings.tsx ; l'ordre d'affichage suit
 * ce catalogue et les valeurs restent calculees des donnees reelles par
 * l'ecran, sur le jeu VIVANT de la porte (insensibles aux filtres et a la
 * recherche).
 */
export const STAT_DEFS: ReadonlyArray<{ id: InventaireStatId; label: string }> = [
  { id: 'total', label: 'materiels' },
  { id: 'disponible', label: 'disponibles' },
  { id: 'loue', label: 'loues' },
  { id: 'maintenance', label: 'en maintenance' },
  { id: 'valeur', label: "valeur d'achat" },
  { id: 'tarif-jour', label: 'potentiel / jour' },
];

/** Nombre maximal de stats affichees (contrat de la cle "app.<id>.stats"). */
export const STATS_MAX = 3;

/** Cle de pref : ids ordonnes des stats affichees. */
export const STATS_PREF_KEY = 'app.inventaire.stats';

/** Defaut stats : les 3 premieres du catalogue. */
export const DEFAULT_STATS: string[] = STAT_DEFS.slice(0, STATS_MAX).map((def) => def.id);
